/**
 * Compliance Radar — dashboard tile listing every employee document expiring
 * in the next 60 days plus any already lapsed. Fetched from
 * GET /api/hr/employee-docs/expiring.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock, FileText } from 'lucide-react';
import { apiClient } from '../../services/api';

const ExpiryRadarTile = ({ days = 60 }) => {
  const [data, setData] = useState({ expiring: [], expired: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiClient.get('/hr/employee-docs/expiring', { params: { days } })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);

  const expiring = data.expiring || [];
  const expired = data.expired || [];
  const rows = [
    ...expired.map((d) => ({ ...d, _kind: 'expired' })),
    ...expiring.map((d) => ({ ...d, _kind: 'expiring' })),
  ].slice(0, 8);

  return (
    <div className="bg-white border border-black/5 rounded-2xl overflow-hidden" data-testid="expiry-radar-tile">
      <div className="p-3 border-b border-black/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <span className="text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70">Compliance radar · {days}d</span>
        </div>
        <Link to="/admin/employee-docs" className="text-[11px] text-[#0f5c3d] hover:underline">Open files</Link>
      </div>
      <div className="flex gap-2 p-3 bg-[#f7fbf8] border-b border-black/5 text-xs">
        <div><span className="font-bold text-red-600">{expired.length}</span> <span className="text-[#0b1a2e]/60">expired</span></div>
        <div><span className="font-bold text-amber-700">{expiring.length}</span> <span className="text-[#0b1a2e]/60">expiring soon</span></div>
      </div>
      {loading ? (
        <div className="p-6 text-center text-xs text-[#0b1a2e]/50">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-center text-xs text-[#0b1a2e]/50">All documents current 👍</div>
      ) : (
        <div className="divide-y divide-black/5 max-h-[280px] overflow-y-auto">
          {rows.map((d) => (
            <div key={d.id} className="p-3 flex items-center gap-2" data-testid={`radar-row-${d.id}`}>
              <FileText className={`w-3.5 h-3.5 ${d._kind === 'expired' ? 'text-red-600' : 'text-amber-600'}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#0b1a2e] truncate">{d.title}</div>
                <div className="text-[11px] text-[#0b1a2e]/50 truncate">{d.user_name || '—'} · {d.employee_code || d.country || ''} · {d.folder}</div>
              </div>
              <div className="text-right">
                <div className={`text-[11px] font-bold ${d._kind === 'expired' ? 'text-red-600' : 'text-amber-700'}`}>{d.expires_on}</div>
                <div className="text-[10px] text-[#0b1a2e]/50 flex items-center justify-end gap-0.5">
                  <Clock className="w-2.5 h-2.5" />
                  {d._kind === 'expired' ? `${Math.abs(d.days_left)}d ago` : `${d.days_left}d left`}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExpiryRadarTile;
